/* eslint-disable no-underscore-dangle */
// Modifications vs original from https://github.com/facebook/lexical/blob/main/packages/lexical-playground/src/nodes/ImageNode.tsx
// 1. Eslint rule disabled to keep the original '__' private variable naming convention.
// 2. Refactored convertImageElement to accept a callback function ($createImageNode) in order to avoid using a function before it was defined.
// 3. Refactored $createImageNode by creating a helper function (buildImageNode) to avoid using a function before it was defined.


// Imports
// ******************************************************************************************************
import { Suspense, lazy, JSX } from 'react';
import {
  $applyNodeReplacement,
  createEditor,
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  NodeKey,
  SerializedEditor,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

const ImageComponent = lazy(() => import('./ImageComponent'));


// Interfaces
// ******************************************************************************************************
export interface ImagePayload {
  altText: string;
  src: string;
  caption?: LexicalEditor;
  height?: number;
  key?: NodeKey;
  showCaption?: boolean;
  width?: number;
}

interface ImageNodeBase {
  altText: string;
  caption: SerializedEditor;
  height?: number;
  showCaption: boolean;
  src: string;
  width?: number;
}

export type SerializedImageNode = Spread<ImageNodeBase, SerializedLexicalNode>;
type BuildImageNode = (payload: ImagePayload, ElClass: typeof ImageNode) => ImageNode;
type ConvertImageElement = (domNode: Node, ElClass: typeof ImageNode) => null | DOMConversionOutput;


// Functions
// ******************************************************************************************************
/**
 * @description Builds an image node. Helper function that creates an image node. It is used to avoid using a function before it was defined
 * in the convertImageElement function. This is a workaround for the eslint rule: https://eslint.org/docs/latest/rules/no-use-before-define
 * @param payload - The payload to use to build the image node.
 * @param ElementClass - The class to use to build the node.
 */
const buildImageNode: BuildImageNode = (
  {
    altText,
    height,
    src,
    width,
    showCaption,
    caption,
    key,
  },
  ElementClass,
) => $applyNodeReplacement(
  new ElementClass(
    src,
    altText,
    width,
    height,
    showCaption,
    caption,
    key,
  ),
);


/**
 * @description Converts an image element to a node.
 * @param domNode
 * @param ElClass - The class to use to build the node.
 */
const convertImageElement: ConvertImageElement = (domNode, ElClass) => {
  if (domNode instanceof HTMLImageElement) {
    const {
      alt: altText, src, width, height,
    } = domNode;
    const node = buildImageNode({
      altText, height, src, width,
    }, ElClass);
    return { node };
  }
  return null;
};


/**
 * @description Creates an image node. Modified version of the original function used in lexical playground.
 */
export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;

  __altText: string;

  __width: 'inherit' | number;

  __height: 'inherit' | number;

  __showCaption: boolean;

  __caption: LexicalEditor;

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__width,
      node.__height,
      node.__showCaption,
      node.__caption,
      node.__key,
    );
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const {
      altText, height, width, caption, src, showCaption,
    } = serializedNode;

    const node = buildImageNode({
      altText,
      height,
      showCaption,
      src,
      width,
    }, ImageNode);

    const nestedEditor = node.__caption;
    const editorState = nestedEditor.parseEditorState(caption.editorState);
    if (!editorState.isEmpty()) {
      nestedEditor.setEditorState(editorState);
    }
    return node;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__altText);
    element.setAttribute('width', this.__width.toString());
    element.setAttribute('height', this.__height.toString());
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (node: Node) => ({
        conversion: () => convertImageElement(node, ImageNode),
        priority: 0,
      }),
    };
  }

  constructor(
    src: string,
    altText: string,
    width?: 'inherit' | number,
    height?: 'inherit' | number,
    showCaption?: boolean,
    caption?: LexicalEditor,
    key?: NodeKey,
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__width = width || 'inherit';
    this.__height = height || 'inherit';
    this.__showCaption = showCaption || false;
    this.__caption = caption || createEditor();
  }

  exportJSON(): SerializedImageNode {
    return {
      altText: this.getAltText(),
      caption: this.__caption.toJSON(),
      height: this.__height === 'inherit' ? 0 : this.__height,
      showCaption: this.__showCaption,
      src: this.getSrc(),
      type: 'image',
      version: 1,
      width: this.__width === 'inherit' ? 0 : this.__width,
    };
  }

  setWidthAndHeight(
    width: 'inherit' | number,
    height: 'inherit' | number,
  ): void {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  // View
  // ************************************************
  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    const { theme } = config;
    const className = theme.image;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }

  updateDOM(): false {
    return false;
  }

  getSrc(): string {
    return this.__src;
  }

  getAltText(): string {
    return this.__altText;
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={null}>
        <ImageComponent
          src={this.__src}
          altText={this.__altText}
          width={this.__width}
          height={this.__height}
          nodeKey={this.getKey()}
          showCaption={this.__showCaption}
          caption={this.__caption}
          resizable
        />
      </Suspense>
    );
  }
}


/**
 * @description Helper function that creates an image node.
 * @param payload - The payload to use to build the image node.
 */
export const $createImageNode = (payload: ImagePayload) => buildImageNode(payload, ImageNode);
